import { adminService } from "@/api/admin/admin.service";
import type { TAdminFile, TOnboardingStatsOverview, TOnboardingAnswer } from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
} from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage, triggerBlobDownload } from "@/utils";
import { Download, FileText, Loader2, Search, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

const PAGE_SIZE = 20;

function StatCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}

export function OnboardingAnswersPage() {
  const { toast } = useToast();

  const [companyIdInput, setCompanyIdInput] = useState("");
  const [companyIdFilter, setCompanyIdFilter] = useState("");

  const [stats, setStats] = useState<TOnboardingStatsOverview | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [files, setFiles] = useState<TAdminFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  // Backend-whitelisted sort columns for admin/onboarding/answers: created_at only.
  const fetcher = async (query: TDataGridQuery) => {
    const { data, error: err } = await adminService.getOnboardingAnswers(
      {
        limit: query.limit,
        offset: query.offset,
        ...(companyIdFilter ? { company_id: companyIdFilter } : {}),
        // Backend search applies to the unfiltered listing only (ignored when company_id is set).
        ...(query.search ? { search: query.search } : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
      },
      { signal: query.signal }
    );
    if (err) throw err;
    return data;
  };

  const loadStats = useCallback(async (signal: AbortSignal) => {
    setStatsLoading(true);
    const { data } = await adminService.getOnboardingStats({ signal });
    if (signal.aborted) return;
    setStats(data?.data ?? null);
    setStatsLoading(false);
  }, []);

  const loadFiles = useCallback(async (companyId: string, signal: AbortSignal) => {
    if (!companyId) {
      setFiles([]);
      setFilesError(null);
      return;
    }
    setFilesLoading(true);
    setFilesError(null);
    const { data, error: err } = await adminService.getOnboardingCompanyFiles(companyId, { signal });
    if (signal.aborted) return;
    if (err) {
      setFilesError(await extractErrorMessage(err));
    } else if (data) {
      setFiles(data.data ?? []);
    }
    setFilesLoading(false);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void loadStats(ctrl.signal);
    return () => ctrl.abort();
  }, [loadStats]);

  useEffect(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    void loadFiles(companyIdFilter, ctrl.signal);
    return () => ctrl.abort();
  }, [loadFiles, companyIdFilter]);

  const applyFilter = () => setCompanyIdFilter(companyIdInput.trim());

  const clearFilter = () => {
    setCompanyIdInput("");
    setCompanyIdFilter("");
  };

  const handleDownloadAll = async () => {
    if (!companyIdFilter) return;
    setIsDownloading(true);
    const { data, error: err } = await adminService.downloadOnboardingCompanyFiles(companyIdFilter);
    if (err) {
      toast({ variant: "destructive", title: "Download failed", description: await extractErrorMessage(err) });
    } else if (data) {
      triggerBlobDownload(data.blob, data.filename ?? `company_${companyIdFilter}_files.zip`);
      toast({ title: "Download started" });
    }
    setIsDownloading(false);
  };

  const columns: TColumnDef<TOnboardingAnswer>[] = [
    { id: "company_id", header: "Company", type: "uuid", accessor: (a) => a.company_id },
    { id: "question_id", header: "Question", type: "uuid", accessor: (a) => a.question_id },
    {
      id: "answer",
      header: "Answer",
      accessor: (a) => a.answer_text ?? a.answer_choice ?? (a.answer_number != null ? String(a.answer_number) : ""),
      cell: (a) => (
        <span className="line-clamp-2 max-w-xs text-foreground">
          {a.answer_text ?? a.answer_choice ?? (a.answer_number != null ? String(a.answer_number) : "—")}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: (a) => (a.status ? <Badge variant="secondary">{a.status}</Badge> : <span className="text-muted-foreground">—</span>),
    },
    {
      id: "has_file",
      header: "File",
      cell: (a) =>
        a.has_file ? (
          <Badge variant="outline">
            <FileText className="mr-1 h-3 w-3" />
            File
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    { id: "created_at", header: "Submitted", type: "date", sortable: true, sortKey: "created_at" },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Onboarding Answers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Review answers submitted by companies and their uploaded files
          </p>
        </div>

        {/* Stats */}
        {statsLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard title="Total Questions" value={String(stats.total_questions ?? 0)} />
            <StatCard title="Total Answers" value={String(stats.total_answers ?? 0)} />
          </div>
        ) : null}

        {/* Company ID filter (drives the answers query and the files sub-panel) */}
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Filter by company ID (UUID)"
              value={companyIdInput}
              onChange={(e) => setCompanyIdInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilter();
              }}
              className="max-w-md flex-1"
            />
            <Button size="sm" onClick={applyFilter}>
              <Search className="mr-2 h-4 w-4" />
              Filter
            </Button>
            {companyIdFilter && (
              <Button size="sm" variant="ghost" onClick={clearFilter}>
                <X className="mr-2 h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Company files (only when filtered) */}
        {companyIdFilter && (
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-foreground">Company Files</h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void handleDownloadAll()}
                disabled={isDownloading || files.length === 0}
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Preparing…
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download all
                  </>
                )}
              </Button>
            </div>
            {filesLoading ? (
              <Skeleton className="h-10 w-full rounded" />
            ) : filesError ? (
              <p className="text-sm text-destructive">{filesError}</p>
            ) : files.length === 0 ? (
              <p className="text-sm text-muted-foreground">No files uploaded by this company.</p>
            ) : (
              <ul className="space-y-1">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <FileText className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{f.original_filename}</span>
                    {f.content_type && (
                      <Badge variant="outline" className="ml-auto">
                        {f.content_type}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DataGrid<TOnboardingAnswer>
          getRowId={(row) => row.id}
          persistKey="admin.onboarding-answers"
          ariaLabel="Onboarding answers"
          data={{ mode: "server", fetcher, deps: [companyIdFilter] }}
          columns={columns}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search answer text or choice",
          }}
          emptyState={{ title: "No answers found." }}
        />
      </div>
    </div>
  );
}
